import { Banner, Card, LegacyStack, Spinner, Text } from '@shopify/polaris'
import { useCallback, useState } from 'react'

import { Result } from '../common/api'
import { ShowIntercomLink, useDialogActivator } from '../common/helpers'
import { ThemeSettings } from '../common/themes'
import { useUiProp } from '../common/ui-props'
import { SellingPlanGroup as SellingPlanGroupFullObj } from '../selling-plan-page/models-common'
import { useSellingPlanGroupsSWR } from '../selling-plans-index-page/api'
import { CloseModal } from './CloseModal'
import { FinalSteps } from './FinalSteps'
import { InstallSubscriptionWidget } from './InstallSubscriptionWidget'
import { SellingPlanCreate } from './SellingPlanCreate'
import { SkipInstallSubscriptionWidgetModal } from './SkipInstallSubscriptionWidgetModal'

export const freeSetupIntercomMessage =
  "I'd like to get the free setup. Can you help me set up my subscriptions?"

export const FreeSetupBanner = () => {
  return (
    <Banner tone="info">
      Feeling lost?{' '}
      <ShowIntercomLink message={freeSetupIntercomMessage}>Get in touch</ShowIntercomLink> and our
      experts will setup PayWhirl in your store for free!
    </Banner>
  )
}

const Content = ({
  mainThemeSettings,
  onComplete,
  onSkipInstallSubscriptionWidget,
}: {
  mainThemeSettings: ThemeSettings
  onComplete: () => void
  onSkipInstallSubscriptionWidget: () => void
}) => {
  const cardProp = useUiProp('getting_started_card_step')
  const [saveResult, setSaveResult] = useState<Result<any> | null>(null)
  const { data: sellingPlans, isValidating: isValidatingSellingPlans } = useSellingPlanGroupsSWR(
    'CREATED_AT_DESC',
    '',
    '',
    '',
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
    }
  )
  const sellingPlanGroups = sellingPlans?.nodes || []

  const gotoStep2 = async (result: Result<SellingPlanGroupFullObj> | null) => {
    setSaveResult(result)
    cardProp.patch('install-subscription-widget')
  }

  return (
    <>
      {!sellingPlans && isValidatingSellingPlans && (
        <LegacyStack distribution="center">
          <Spinner size="large" />
        </LegacyStack>
      )}
      {sellingPlans && (
        <>
          {cardProp.value === 'create-selling-plan' && (
            <SellingPlanCreate
              hasSellingPlans={sellingPlanGroups.length > 0}
              saveResult={saveResult}
              setSaveResult={setSaveResult}
              onComplete={gotoStep2}
            />
          )}
          {cardProp.value === 'install-subscription-widget' && (
            <InstallSubscriptionWidget
              onComplete={() => cardProp.patch('final-steps')}
              onSkip={onSkipInstallSubscriptionWidget}
            />
          )}
          {cardProp.value === 'final-steps' && <FinalSteps onComplete={onComplete} />}
        </>
      )}
    </>
  )
}

export const GettingStarted = ({ mainThemeSettings }: { mainThemeSettings: ThemeSettings }) => {
  const closeModalActivator = useDialogActivator()
  const skipInstallSubscriptionWidgetModalActivator = useDialogActivator()

  const gettingStartedCardProp = useUiProp('getting_started_card_step')

  const dismissGettingStarted = useCallback(async () => {
    if (closeModalActivator.open) {
      closeModalActivator.close()
    }
    gettingStartedCardProp.patch('completed')
  }, [gettingStartedCardProp, closeModalActivator])

  const handleSkipInstallSubscriptionWidget = () => {
    if (mainThemeSettings.subscriptionWidgetInstalled) {
      gettingStartedCardProp.patch('final-steps')
      return
    }
    skipInstallSubscriptionWidgetModalActivator.show()
  }

  return (
    <>
      <Card>
        <Content
          mainThemeSettings={mainThemeSettings}
          onComplete={closeModalActivator.show}
          onSkipInstallSubscriptionWidget={handleSkipInstallSubscriptionWidget}
        />
      </Card>
      <CloseModal activator={closeModalActivator} onDismiss={dismissGettingStarted}>
        <Text as="p">
          You can restart this wizard by clicking <strong>Quick Start</strong> in the Dashboard page
          menu.
        </Text>
      </CloseModal>
      <SkipInstallSubscriptionWidgetModal
        modalActivator={skipInstallSubscriptionWidgetModalActivator}
        handleModalClose={() => {
          skipInstallSubscriptionWidgetModalActivator.close()
          gettingStartedCardProp.patch('final-steps')
        }}
        handleGoBack={() => {
          skipInstallSubscriptionWidgetModalActivator.close()
        }}
      />
    </>
  )
}
